import { FormSection, FormRow, MediumFormRow, BigFormRow } from 'framework/ui/layout';
import { useEffect, useState } from 'react';
import LeaveTypeDropdown from 'domain/people/leave/shared/input/LeaveTypeDropdown';
import LeaveStatusDropdown from 'domain/people/leave/shared/input/LeaveStatusDropdown';
import ActualEndDateInput from 'domain/people/leave/shared/input/ActualEndDateInput';
import { hasSignatureOption } from 'domain/people/leave/shared/leaveService';
import { useAuthorization } from 'framework/authorization';
import LeaveDurationWidget from 'domain/people/leave/createupdate/duration';
import SubTypeReasonDropdown from 'domain/people/leave/shared/input/SubTypeReasonDropdown';
import SignatureOptionDropdown from 'domain/people/leave/shared/input/SignatureOptionDropdown';
import SignatureOptionTooltip from 'domain/people/leave/shared/tooltip/SignatureOptionTooltip';
import {
  isMinskOffice,
  useEmployeeService,
  EmployeeInput,
  LeaveApplicationScanUploaderInput,
} from 'domain/shared/employee';
import {
  IS_PHYSICAL_OPTION_ALIASES,
  LEAVE_STATUSES_ALIASES,
  LEAVE_TYPES_ALIASES,
  LEAVE_UPDATE,
  LeaveFormData,
} from 'domain/shared/leave';
import { canSelectMaternityLeaveType } from 'domain/people/leave/shared/maternityLeaveEligibility';
import { useFormContext, DateInput, ClearAfterUnmountWrapper, TextArea, Checkbox } from 'framework/form';
import { useAsyncValue } from 'framework/utils';

const isRegistered = (status?: string) => {
  return LEAVE_STATUSES_ALIASES.REGISTERED === status;
};

const shouldShowActualEndDate = (leave: LeaveFormData) => {
  return (
    !!leave.allowedExtensionDate &&
    (leave.type === LEAVE_TYPES_ALIASES.PAID_LEAVE || leave.type === 'COMPENSATION') &&
    isRegistered(leave.status)
  );
};

const shouldShowElectronicOption = ({ signatureOption }: LeaveFormData) => {
  return signatureOption === IS_PHYSICAL_OPTION_ALIASES.ELECTRONIC;
};

const shouldShowSubTypeReason = (leave: LeaveFormData, canUseMaternityLeave: boolean) => {
  return (
    leave.type === LEAVE_TYPES_ALIASES.PAID_LEAVE ||
    leave.type === LEAVE_TYPES_ALIASES.SICK_DAY ||
    leave.type === LEAVE_TYPES_ALIASES.LEAVE_FOR_PERSONAL_CIRCUMSTANCES ||
    (leave.type === LEAVE_TYPES_ALIASES.MATERNITY_LEAVE && canUseMaternityLeave)
  );
};

const LeaveForm = ({ isOneStop = false, isEdit = false }) => {
  const { data } = useFormContext();
  const { isGranted } = useAuthorization();
  const [initialStatus, setInitialStatus] = useState<string | undefined>();

  useEffect(() => {
    if (!initialStatus && data?.status) {
      setInitialStatus(data.status);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { getById } = useEmployeeService();
  const employee = useAsyncValue(
    async () => (isOneStop ? getById() : data?.employeeAlias && getById(data?.employeeAlias)),
    [data?.employeeAlias],
  );
  const isFormLocked = isRegistered(initialStatus);

  const isMinskEmployee = employee?.officeId && isMinskOffice(String(employee.officeId));

  const canUseMaternityLeave = canSelectMaternityLeaveType(employee?.officeId, employee?.contractType);

  const endDateHelpText = isMinskEmployee
    ? 'For Sick leave or Military leave, please enter the earliest expected end date.'
    : 'For Sick leave, please enter the earliest expected end date.';

  return (
    <>
      <FormSection title="Leave information">
        <FormRow>
          <EmployeeInput
            source="employeeAlias"
            label="Worker"
            isInitialNameLoad
            isVisible={!isOneStop}
            disabled={isFormLocked}
          />
          <LeaveTypeDropdown
            source="type"
            label="Leave type"
            disabled={isFormLocked}
            isOneStop={isOneStop}
            employee={employee}
          />
          <LeaveStatusDropdown
            source="status"
            label="Leave status"
            isVisible={!isOneStop}
            disabled={isFormLocked && !isGranted(LEAVE_UPDATE)}
          />

          <ClearAfterUnmountWrapper
            isVisible={(leave: LeaveFormData) => shouldShowSubTypeReason(leave, canUseMaternityLeave)}
          >
            <SubTypeReasonDropdown
              sourceType="type"
              source="subType"
              label="Reason"
              employee={employee ?? undefined}
              disabled={isFormLocked}
            />
          </ClearAfterUnmountWrapper>
        </FormRow>
        <FormRow>
          <DateInput source="startDate" label="Start date" disabled={isFormLocked} />
          <DateInput source="endDate" label="End date" disabled={isFormLocked} helpText={endDateHelpText} />
          <ClearAfterUnmountWrapper isVisible={(leave: LeaveFormData) => shouldShowActualEndDate(leave)}>
            <ActualEndDateInput
              source="extendedEndDate"
              label="Actual end date"
              helpText="Worker has sick leave(s) within the period of the paid leave.
              So, the leave can be extended without duration changing."
            />
          </ClearAfterUnmountWrapper>
          <LeaveDurationWidget />
        </FormRow>
        <MediumFormRow>
          <LeaveApplicationScanUploaderInput
            label="Application scans"
            source="applicationScansIds"
            isVisible={!isOneStop}
            disabled={isFormLocked}
          />
        </MediumFormRow>
      </FormSection>
      <FormSection title="Signature option" isVisible={leave => hasSignatureOption(leave)}>
        <BigFormRow>
          <ClearAfterUnmountWrapper>
            <SignatureOptionDropdown
              source="signatureOption"
              disabled={isFormLocked}
              tooltip={<SignatureOptionTooltip />}
              label="Will you be able to arrive at the office for signing documents
            within 2 business days after leave approval?"
            />
          </ClearAfterUnmountWrapper>
        </BigFormRow>
        <MediumFormRow>
          <ClearAfterUnmountWrapper isVisible={shouldShowElectronicOption}>
            <TextArea
              source="electronicOptionReason"
              label="Please describe the reason why you are not able to arrive at the office"
              disabled={isFormLocked}
            />
          </ClearAfterUnmountWrapper>
        </MediumFormRow>
        <BigFormRow>
          <ClearAfterUnmountWrapper isVisible={shouldShowElectronicOption}>
            <Checkbox
              source="electronicOptionAgreement"
              label="I confirm that the company can use my comment in the dedicated Redmine ticket as a replacement for my physical signature in leave documents"
              isVisible={shouldShowElectronicOption}
              disabled={isFormLocked}
            />
          </ClearAfterUnmountWrapper>
        </BigFormRow>
      </FormSection>
    </>
  );
};

export default LeaveForm;
